export function useUrlParams() {
    /**
     * Hydrate the target object with values from the URL parameters.
     * @param {*} target - The target object to hydrate.
     * @param {object} mapping - The mapping configuration for the hydration.
     * @param {object} props - Additional properties to use during hydration.
     * @returns {*} - The hydrated target object.
     */
    const hydrate = (target, mapping, props = {}) => {
        const urlParams = new URL(window.location).searchParams;
        
        Object.keys(mapping).forEach(key => {
            const config = mapping[key];
            const urlKey = config.urlKey || key;
            
            switch (config.type) {
                case 'number':
                    if (urlParams.get(urlKey)) {
                        target[key] = parseInt(urlParams.get(urlKey));
                    }
                    break;
                    
                case 'string':
                    if (urlParams.get(urlKey)) {
                        target[key] = urlParams.get(urlKey);
                    }
                    break;
                    
                case 'boolean':
                    if (urlParams.get(urlKey)) {
                        target[key] = urlParams.get(urlKey) === 'true';
                    }
                    break;
                    
                case 'array':
                    let arrayValues = urlParams.getAll(urlKey + '[]');
                    
                    if (arrayValues.length === 0) {
                        const allParams = Array.from(urlParams.entries());
                        arrayValues = allParams
                            .filter(([k]) => k.startsWith(urlKey + '['))
                            .map(([, value]) => value);
                    }
                    
                    if (arrayValues.length === 0) break;
                    
                    if (config.valueType === 'number') {
                        arrayValues = arrayValues.map(Number).filter(Boolean);
                    } else if (config.valueType === 'boolean') {
                        arrayValues = arrayValues.map(v => v === 'true');
                    }
                    
                    if (config.hydrate) {
                        target[key] = config.hydrate(arrayValues, props);
                    } else if (config.mapFrom && props[config.mapFrom]) {
                        const mapKey = config.mapKey || 'id';
                        const source = props[config.mapFrom];
                        const sourceArray = Array.isArray(source) ? source : [source];
                        
                        target[key] = sourceArray.filter(item => 
                            arrayValues.includes(item[mapKey])
                        );
                    } else {
                        target[key] = arrayValues;
                    }
                    break;
                    
                case 'object':
                    if (config.keys) {
                        // Preserve existing object or create new one
                        const obj = target[key] && typeof target[key] === 'object' ? { ...target[key] } : {};
                        let hasValues = false;
                        
                        Object.keys(config.keys).forEach(objKey => {
                            const keyConfig = config.keys[objKey];
                            
                            // Default to nested format: date[from], date[to]
                            let paramKey = `${urlKey}[${objKey}]`;
                            let valueType = 'string';
                            
                            if (typeof keyConfig === 'string') {
                                // If it's a type string, use it as the type
                                if (['string', 'number', 'boolean'].includes(keyConfig)) {
                                    valueType = keyConfig;
                                    // paramKey stays as nested format
                                } else {
                                    // It's a custom param name mapping
                                    paramKey = keyConfig;
                                }
                            } else if (typeof keyConfig === 'object') {
                                // Object format: { urlKey: 'custom_name', type: 'string' }
                                paramKey = keyConfig.urlKey || `${urlKey}[${objKey}]`;
                                valueType = keyConfig.type || 'string';
                            }
                            
                            const urlValue = urlParams.get(paramKey);
                            if (urlValue) {
                                hasValues = true;
                                
                                // Convert based on type
                                switch (valueType) {
                                    case 'number':
                                        obj[objKey] = parseInt(urlValue);
                                        break;
                                    case 'boolean':
                                        obj[objKey] = urlValue === 'true';
                                        break;
                                    case 'string':
                                    default:
                                        obj[objKey] = urlValue;
                                        break;
                                }
                            }
                        });
                        
                        // Only update if we found URL values, otherwise preserve existing
                        if (hasValues) {
                            target[key] = obj;
                        }
                    }
                    break;
            }
        });
        
        return target;
    };
    /**
     * Serialize the data into URL parameters.
     * @param {object} pendingVisit - The pending visit object.
     * @param {object} data - The data to serialize.
     * @param {object} mapping - The mapping configuration.
     * @param {object} options - Additional options for serialization.
     */
    const serialize = (pendingVisit, data, mapping, options = {}) => {
        const { skip = [] } = options;
        
        // Helper to check if a path should be skipped
        const shouldSkip = (path) => skip.includes(path);
        
        // Helper to get nested skip keys for an object
        const getNestedSkips = (parentKey) => {
            return skip
                .filter(s => s.startsWith(parentKey + '.'))
                .map(s => s.substring(parentKey.length + 1));
        };
        
        Object.keys(mapping).forEach(key => {
            if (shouldSkip(key)) return;
            
            const config = mapping[key];
            const urlKey = config.urlKey || key;
            const value = data[key];
            
            if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
                Array.from(pendingVisit.url.searchParams.keys())
                    .filter(k => k === urlKey || k.startsWith(urlKey + '['))
                    .forEach(k => pendingVisit.url.searchParams.delete(k));
                return;
            }
            
            switch (config.type) {
                case 'number':
                case 'string':
                case 'boolean':
                    pendingVisit.url.searchParams.set(urlKey, value.toString());
                    break;
                    
                case 'array':
                    Array.from(pendingVisit.url.searchParams.keys())
                        .filter(k => k === urlKey || k.startsWith(urlKey + '['))
                        .forEach(k => pendingVisit.url.searchParams.delete(k));
                    
                    if (Array.isArray(value) && value.length > 0) {
                        let values = value;
                        
                        if (config.serialize) {
                            values = config.serialize(value);
                        } else if (config.mapKey && typeof value[0] === 'object') {
                            values = value.map(item => item[config.mapKey]);
                        }
                        
                        values.forEach(v => {
                            pendingVisit.url.searchParams.append(urlKey + '[]', v);
                        });
                    }
                    break;
                    
                case 'object':
                    if (config.keys) {
                        const nestedSkips = getNestedSkips(key);
                        
                        // First, handle skipped keys - always delete them from URL
                        nestedSkips.forEach(objKey => {
                            const keyConfig = config.keys[objKey];
                            if (!keyConfig) return;
                            
                            let paramKey = `${urlKey}[${objKey}]`;
                            
                            if (typeof keyConfig === 'string') {
                                if (!['string', 'number', 'boolean'].includes(keyConfig)) {
                                    paramKey = keyConfig;
                                }
                            } else if (typeof keyConfig === 'object') {
                                paramKey = keyConfig.urlKey || `${urlKey}[${objKey}]`;
                            }
                            
                            pendingVisit.url.searchParams.delete(paramKey);
                        });
                        
                        // Then handle non-skipped keys
                        if (typeof value === 'object') {
                            Object.keys(config.keys).forEach(objKey => {
                                // Skip if this key should be skipped
                                if (nestedSkips.includes(objKey)) return;
                                
                                const keyConfig = config.keys[objKey];
                                
                                // Use nested format by default: date[from], date[to]
                                let paramKey = `${urlKey}[${objKey}]`;
                                
                                if (typeof keyConfig === 'string') {
                                    if (!['string', 'number', 'boolean'].includes(keyConfig)) {
                                        paramKey = keyConfig; // It's a custom param name mapping
                                    }
                                } else if (typeof keyConfig === 'object') {
                                    paramKey = keyConfig.urlKey || `${urlKey}[${objKey}]`;
                                }
                                
                                if (value[objKey] != null && value[objKey] !== '') {
                                    pendingVisit.url.searchParams.set(paramKey, value[objKey].toString());
                                } else {
                                    // Only delete the specific parameter, not all nested params
                                    pendingVisit.url.searchParams.delete(paramKey);
                                }
                            });
                        } else {
                            // Value is null/undefined - clear only the non-skipped mapped keys
                            Object.keys(config.keys).forEach(objKey => {
                                if (nestedSkips.includes(objKey)) return;
                                
                                const keyConfig = config.keys[objKey];
                                let paramKey = `${urlKey}[${objKey}]`;
                                
                                if (typeof keyConfig === 'string' && !['string', 'number', 'boolean'].includes(keyConfig)) {
                                    paramKey = keyConfig;
                                } else if (typeof keyConfig === 'object') {
                                    paramKey = keyConfig.urlKey || `${urlKey}[${objKey}]`;
                                }
                                
                                pendingVisit.url.searchParams.delete(paramKey);
                            });
                        }
                    }
                    break;
            }
        });
    };
    
    return { hydrate, serialize };
}